/**
 * Report API functions
 * All report operations require authentication
 */

/**
 * Report reasons with user-friendly labels
 */
export const REPORT_REASONS = [
    { value: 'spam', label: 'Spam or misleading' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'misinformation', label: 'Misinformation' },
    { value: 'hate-speech', label: 'Hate speech' },
    { value: 'off-topic', label: 'Off-topic content' },
    { value: 'other', label: 'Other' },
];

/**
 * Create a new report for a post (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {object} reportData - Report data { postId, communityId, reason }
 * @returns {Promise<object>} - Created report with success message
 */
export const createReport = async (axiosInstance, reportData) => {
    const response = await axiosInstance.post('/api/reports', reportData);
    return response.data;
};

/**
 * Check if current user has already reported a post (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} postId - Post ID to check
 * @returns {Promise<object>} - { hasReported: boolean }
 */
export const checkReport = async (axiosInstance, postId) => {
    const response = await axiosInstance.get(`/api/reports/check/${postId}`);
    return response.data;
};

/**
 * Get all reported posts across every community (admin only).
 * Community moderators use a separate endpoint (/api/reports/moderator,
 * called directly from the moderator dashboard) that scopes results to
 * the communities they moderate.
 * @param {object} axiosInstance - Authenticated axios instance
 * @returns {Promise<Array>} - Array of reported post groups
 */
export const getReportedPosts = async (axiosInstance) => {
    const response = await axiosInstance.get('/api/reports/admin');
    return response.data;
};

export const getReportedReplies = async (axiosInstance) => {
    const response = await axiosInstance.get('/api/reply-reports/admin');
    return response.data;
};

/**
 * Dismiss a single report (admin or community moderator).
 * Community moderators can only dismiss reports for posts in their community.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} reportId - Report ID to dismiss
 * @returns {Promise<object>} - Dismissed report with success message
 */
export const dismissReport = async (axiosInstance, reportId) => {
    const response = await axiosInstance.put(
        `/api/reports/${reportId}/dismiss`
    );
    return response.data;
};

export const dismissReplyReport = async (axiosInstance, reportId) => {
    const response = await axiosInstance.put(
        `/api/reply-reports/${reportId}/dismiss`
    );
    return response.data;
};

/**
 * Dismiss all pending reports for a post (admin or community moderator).
 * Community moderators can only dismiss reports for posts in their community.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} postId - Post ID whose reports should be dismissed
 * @returns {Promise<object>} - Success message with dismissed count
 */
export const dismissReportsByPost = async (axiosInstance, postId) => {
    const response = await axiosInstance.put(
        `/api/reports/post/${postId}/dismiss`
    );
    return response.data;
};

export const dismissReportsByReply = async (axiosInstance, replyId) => {
    const response = await axiosInstance.put(
        `/api/reply-reports/reply/${replyId}/dismiss`
    );
    return response.data;
};

/**
 * Get all posts pending human review (admin only)
 * These are posts saved with status "pending_review" when the moderation engine was unreachable.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Results per page (default 20)
 * @returns {Promise<object>} - { posts, total, page, totalPages }
 */
export const getPendingReviewPosts = async (
    axiosInstance,
    page = 1,
    limit = 20
) => {
    const response = await axiosInstance.get('/api/posts/pending-review', {
        params: { page, limit },
    });
    return response.data;
};

/**
 * Get flagged or pending-review posts for a specific community.
 * Site admins can view any community's queue; community moderators only
 * the queue for a community they moderate. Used when scoping the
 * flagged-content queue to a single community.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} communityId - The community to fetch flagged content for
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Results per page (default 20)
 * @returns {Promise<object>} - { posts, total, page, totalPages }
 */
export const getFlaggedPostsForCommunity = async (
    axiosInstance,
    communityId,
    page = 1,
    limit = 20
) => {
    const response = await axiosInstance.get(
        `/api/posts/flagged/${communityId}`,
        {
            params: { page, limit },
        }
    );
    return response.data;
};

/**
 * Get flagged or pending-review posts across every community the requester
 * moderates, in a single response. Mirrors the shape of the reports
 * moderator endpoint so the moderator dashboard can fetch both queues with
 * one round-trip each. Each post is enriched server-side with its
 * communityName for dashboard provenance.
 *
 * Returns an empty result for users with no active moderator memberships
 * (including site admins who don't happen to moderate any community — they
 * should use the per-community variant to scope by a specific community).
 *
 * Default limit is 100 (the server-side cap) rather than 20 because the
 * dashboard renders a single combined queue with no paging UI today. The
 * old per-community fan-out implicitly summed limit×N across communities,
 * so a moderator of N communities used to see up to 20×N posts. A future
 * pagination ticket can lower the default + add page controls.
 *
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Results per page (default 100, server caps at 100)
 * @returns {Promise<object>} - { posts, total, page, totalPages }
 */
export const getFlaggedPostsForModerator = async (
    axiosInstance,
    page = 1,
    limit = 100
) => {
    const response = await axiosInstance.get('/api/posts/flagged', {
        params: { page, limit },
    });
    return response.data;
};

export const getModeratorReplyReports = async (axiosInstance) => {
    const response = await axiosInstance.get('/api/reply-reports/moderator');
    return response.data;
};

/**
 * Moderate a post — approve, hide, or delete.
 * Authorization:
 *   - approve: site admin only (pending-review queue is admin-managed)
 *   - hide / delete: site admin OR community moderator of the post's community
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} postId - Post ID to moderate
 * @param {string} action - 'approve', 'hide', or 'delete'
 * @returns {Promise<object>} - Success message
 */
export const moderatePost = async (axiosInstance, postId, action) => {
    const response = await axiosInstance.put(`/api/posts/${postId}/moderate`, {
        action,
    });
    return response.data;
};

/**
 * Get all replies pending human review (admin only)
 * These are replies saved with status "pending_review" when the moderation engine was unreachable.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Results per page (default 100)
 * @returns {Promise<object>} - { replies, total, page, totalPages }
 */
export const getPendingReviewReplies = async (
    axiosInstance,
    page = 1,
    limit = 100
) => {
    const response = await axiosInstance.get('/api/replies/pending-review', {
        params: { page, limit },
    });
    return response.data;
};

/**
 * Moderate a reply — approve or delete (admin only)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} replyId - Reply ID to moderate
 * @param {string} action - 'approve' or 'delete'
 * @returns {Promise<object>} - Success message
 */
export const moderateReply = async (axiosInstance, replyId, action) => {
    const response = await axiosInstance.put(
        `/api/replies/${replyId}/moderate`,
        { action }
    );
    return response.data;
};
